<script lang="ts" setup>
import { CalendarList } from '@/class/pages/CalendarList'
import { GroupList } from '@/class/pages/GroupList'
import { CalendarGroupService } from '@/class/services/CalendarGroupService'
import DeleteConfirmation from '@/components/common/DeleteConfirmation.vue'
import GeneralConfirmation from '@/components/common/GeneralConfirmation.vue'
import CreateGroupModal from '@/components/Settings/CreateGroupModal.vue'
import RearrangeCalendarsModal from '@/components/Settings/RearrangeCalendarsModal.vue'
import Team from '@/models/team'
import AppState, {
  getCalendarsInLocation,
  getTeamsInLocation,
  isCreateGroupDisabled,
  isPermissionsEnabled,
  updateCalendarStore,
  updateTeamsStore,
} from '@/states/app'
import {
  AlertHexagonIcon,
  Edit01Icon,
  MoveIcon,
  PlusIcon,
  Share06Icon,
  Trash03Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  renderIcon,
  UIActionDropdown,
  UIButton,
  UICheckbox,
  UISpin,
  UITooltip,
} from '@gohighlevel/ghl-ui'
import { computed, h, PropType, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { CalendarScopes, ICalendarShare, SHARE_SOURCE } from '../../types'
import CalendarShareModal from './CalendarShareModal.vue'

const { t } = useI18n()

const props = defineProps({
  groupFilter: {
    type: String,
    required: false,
  },
  onUpdateGroupFilter: {
    type: Function,
    required: false,
  },
  calendarList: {
    type: CalendarList,
    required: true,
  },
  type: {
    type: String as PropType<'service' | 'sales'>,
    required: false,
  },
  groupList: {
    type: GroupList,
    required: true,
  },
})

const selectedGroup = ref()
const showDeleteConfirmationModal = ref(false)
const showDeactivateConfirmationModal = ref(false)
const isProcessing = ref(false)
const showNewGroupModal = ref(false)
const showEditGroupModal = ref(false)
const showRearrangeModal = ref(false)
const groupEditPayload = ref(null as any)
const deleteCalendarsCheckBoxValue = ref(false)
const showShareModal = ref(false)
const isLoading = ref(false)
const calendarList = props.calendarList
const unnassignedGroup = computed(() => {
  return calendarList.filterdCalendars.filter(
    (calendar: any) => !calendar.providerId
  )
})

const tableActionsOptionsForGroups = [
  {
    label: t('settings.calendar.edit_group'),
    key: 'edit',
    icon: renderIcon(Edit01Icon),
  },
  {
    label: t('settings.calendar.share_group'),
    key: 'share_group',
    icon: renderIcon(Share06Icon),
  },
  {
    label: t('settings.calendar.rearrange_calendars'),
    key: 'rearrange_calendars',
    icon: renderIcon(MoveIcon),
  },
  {
    label: t('settings.calendar.deactivate_all_calendars_in_group'),
    key: 'toggle_group',
    icon: renderIcon(AlertHexagonIcon),
  },
  {
    label: t('settings.calendar.delete_group'),
    key: 'delete_group',
    icon() {
      return h(renderIcon(Trash03Icon), { class: '!text-red-600' })
    },
    props: { class: 'border-t-[1px] !text-red-600' },
  },
]

const tableActionsOptionsForDisabledGroups = tableActionsOptionsForGroups.map(
  x => x
)

tableActionsOptionsForDisabledGroups[3] = {
  label: t('settings.calendar.activate_all_calendars_in_group'),
  key: 'toggle_group',
  icon: renderIcon(AlertHexagonIcon),
}

const tableActionDropdownOptions = (row: any) => {
  const options = row.isActive
    ? tableActionsOptionsForGroups.filter(
        x => x.key !== 'rearrange_calendars' || row.nosOfCalendars > 0
      )
    : tableActionsOptionsForDisabledGroups.filter(
        x => x.key !== 'rearrange_calendars' || row.nosOfCalendars > 0
      )

  if (!isPermissionsEnabled(CalendarScopes.EDIT_CALENDAR_GROUPS)) {
    return options.filter(
      x =>
        ![
          'edit',
          'delete_group',
          'rearrange_calendars',
          'toggle_group',
        ].includes(x.key)
    )
  }

  return options
}

const dropDownActionChange = (action, team) => {
  if (action === 'edit') {
    openEditGroupModal(team)
  } else if (action === 'share_group') {
    selectedGroup.value = team
    showShareModal.value = true
  } else if (action === 'delete_group') {
    selectedGroup.value = team
    showDeleteConfirmationModal.value = true
  } else if (action === 'rearrange_calendars') {
    selectedGroup.value = team
    showRearrangeModal.value = true
  } else if (action === 'toggle_group') {
    selectedGroup.value = team
    showDeactivateConfirmationModal.value = true
  }
}

const toggleShowDeleteModal = () => {
  showDeleteConfirmationModal.value = !showDeleteConfirmationModal.value
  deleteCalendarsCheckBoxValue.value = false
}

const closeModal = () => {
  showDeleteConfirmationModal.value = false
  showDeactivateConfirmationModal.value = false
  deleteCalendarsCheckBoxValue.value = false
  showRearrangeModal.value = false
  showShareModal.value = false
  showNewGroupModal.value = false
  showEditGroupModal.value = false
  isProcessing.value = false
}

const deleteGroup = async () => {
  if (!selectedGroup.value) {
    return
  }
  isProcessing.value = true
  await CalendarGroupService.delete(
    selectedGroup.value.id,
    deleteCalendarsCheckBoxValue.value
  )
  const fetchCalendars = deleteCalendarsCheckBoxValue.value
  props.calendarList.filters.value.searchText = ''
  props.groupList.filters.value.searchText = ''
  await Promise.all([getTeamsInLocation(true), getCalendarsInLocation(true)])
  updateTeamsStore()
  if (fetchCalendars) {
    updateCalendarStore()
  }
  emit('update:groupFilter', null)
  onClickGroup(null)
  closeModal()
  selectedGroup.value = null
  isProcessing.value = false
}

const closeGroupModal = () => {
  showNewGroupModal.value = false
  showEditGroupModal.value = false
}

const editGroupSuccess = async (
  groupData: any,
  isNew: boolean,
  fetchCalendars = false
) => {
  closeGroupModal()
  isProcessing.value = true
  const fetchPromises = [getTeamsInLocation(true)]
  if (fetchCalendars) {
    fetchPromises.unshift(getCalendarsInLocation(true)) // Ensures calendars are fetched first
  }
  await Promise.all(fetchPromises)
  if (isNew) {
    Object.assign(AppState, {
      showGroupSuccessAlert: true,
      createdGroup: groupData,
    })
  }
  updateTeamsStore()
  isProcessing.value = false
}

const openEditGroupModal = (team: Team | null) => {
  showNewGroupModal.value = true
  if (team) {
    groupEditPayload.value = team
  } else {
    groupEditPayload.value = null
  }
}

const closeShareCalendarPopup = () => {
  showShareModal.value = false
}

const selectedGroupData = computed(() => {
  const group = selectedGroup.value
  const data: ICalendarShare = {
    id: group?.id,
    slug: group?.slug,
    name: group?.name,
    calendarType: group?.calendarType,
    schedulePath: 'groups',
    permanentPath: 'group',
    source: SHARE_SOURCE.GROUP,
    showOneTimeLink: false,
  }
  return data
})

const emit = defineEmits(['update:groupFilter'])

const onClickGroup = (group: Team | null | 'unassigned') => {
  if (!group) {
    props.calendarList.filters.value.searchText = ''
    props.groupList.filters.value.searchText = ''
  }
  if (group === 'unassigned') {
    emit('update:groupFilter', 'unassigned')
  } else {
    emit('update:groupFilter', group?.id)
  }
  selectedGroup.value = group
  if (group && group !== 'unassigned') {
    storeTeams(group)
  }
}

const disableGroup = async () => {
  if (!selectedGroup.value) {
    return
  }

  isProcessing.value = true

  await CalendarGroupService.updateStatus(
    selectedGroup.value.id,
    !selectedGroup.value.isActive
  )
  await Promise.all([getTeamsInLocation(true), getCalendarsInLocation(true)])
  updateTeamsStore()
  updateCalendarStore()
  closeModal()
  isProcessing.value = false
}
const newGroupCreated = (groupData: any, isNew: boolean) => {
  const allCalendars = AppState.globalCalendars?.calendars || []
  getTeamsInLocation(true)
  closeGroupModal()
  AppState.selectedTab = 'calendars'
  if (isNew && allCalendars?.length > 0) {
    AppState.showGroupSuccessAlert = true
    AppState.createdGroup = groupData
  }
  updateTeamsStore()
}

const getLenghtText = (length: number) => {
  if (length === 0) {
    return '0'
  }
  if (length < 10) {
    return '0' + length
  }
  return length
}

const storeTeams = (team: Team) => {
  const teams = props.groupList.teamsFromLocalStorage || []
  if (!teams.includes(team.id)) {
    teams.unshift(team.id)
    if (teams.length > 5) {
      teams.pop()
    }
    props.groupList.setTeamsInLocalStorage(teams)
  }
}
</script>
<template>
  <div class="w-[250px]">
    <div v-if="isLoading || AppState.isLoading">
      <UISpin
        class="flex h-[300px] items-center justify-center overflow-auto"
      />
    </div>
    <div v-else>
      <CreateGroupModal
        :show="showNewGroupModal"
        :edit-payload="groupEditPayload"
        @created-record="editGroupSuccess"
        @close-modal="closeGroupModal"
      />
      <RearrangeCalendarsModal
        :team-id="selectedGroup?.id"
        :show="showRearrangeModal"
        :group-name="selectedGroup?.name"
        @close-modal="() => (showRearrangeModal = false)"
      />

      <div
        class="mb-[5px] flex flex-row items-center justify-between rounded-md px-[8px] py-[10px]"
        :class="{
          'bg-primary-50 font-normal text-primary-700': !selectedGroup,
          'font-normal text-gray-500': selectedGroup?.id,
          'text-gray-500': !selectedGroup?.id,
        }"
        role="button"
        @click="onClickGroup(null)"
        @keydown="onClickGroup(null)"
      >
        <div class="w-full cursor-pointer rounded-md">
          {{ t('settings.calendar.all_calendars') }} ({{
            getLenghtText(calendarList.filterdCalendars.length)
          }})
        </div>
      </div>
      <div class="mr-[8px] mt-[10px] w-full border-t border-gray-200" />
      <div class="text-gray-90 pb-2 pl-1 pt-4 text-[16px] font-normal">
        {{ t('settings.calendar.groups') }}
      </div>

      <div class="max-h-[300px] overflow-y-auto">
        <div
          class="flex w-full cursor-pointer flex-row items-center justify-between gap-1 rounded-md py-[10px] pl-[5px] pr-[14px]"
          :class="{
            'bg-primary-50 font-normal text-primary-700':
              selectedGroup === 'unassigned',
            'font-normal text-gray-500': selectedGroup !== 'unassigned',
          }"
        >
          <div
            class="flex w-[80%] cursor-pointer flex-row items-center gap-4"
            role="button"
            @click="onClickGroup('unassigned')"
            @keydown="onClickGroup('unassigned')"
          >
            <div class="flex w-full flex-row items-center gap-2">
              <UITooltip placement="top">
                <template #trigger>
                  <div class="max-w-24 w-fit truncate">
                    {{ $t('settings.calendar.not_grouped') }}
                  </div>
                </template>
                {{ $t('settings.calendar.not_grouped') }}
              </UITooltip>
              <div
                class="flex items-center justify-center rounded-2xl bg-gray-200 px-[6px] py-[2px] text-[10px] font-normal text-gray-500"
              >
                {{ getLenghtText(unnassignedGroup.length || 0) }}
              </div>
            </div>
          </div>
        </div>
        <div
          v-for="(step, index) in groupList.teamsForCalendars"
          id="step-container"
          :key="index"
          active-class="active"
        >
          <div
            class="flex w-auto cursor-pointer flex-row items-center justify-between gap-1 rounded-md py-[5px] pl-[5px] pr-[14px] text-[14px]"
            :class="{
              'bg-primary-50 font-normal text-primary-700':
                selectedGroup?.id === step.id,
              'font-normal text-gray-500': selectedGroup?.id !== step.id,
            }"
          >
            <div
              class="flex w-[80%] cursor-pointer flex-row items-center gap-4"
              role="button"
              @click="onClickGroup(step)"
              @keydown="onClickGroup(step)"
            >
              <div class="flex w-full flex-row gap-2">
                <UITooltip placement="top">
                  <template #trigger>
                    <div class="max-w-26 w-fit truncate">
                      {{ step.name }}
                    </div>
                  </template>
                  {{ step.name }}
                </UITooltip>
                <div
                  class="flex w-[10%] items-center justify-center rounded-2xl bg-gray-200 px-[6px] py-[2px] text-[10px] font-normal text-gray-500"
                >
                  {{ getLenghtText(calendarList.groupWiseCount[step.id] || 0) }}
                </div>
              </div>
            </div>

            <UIActionDropdown
              :id="`calendar-group-${step.id}`"
              :aria-label="$t('aria_labels.category_actions')"
              :options="tableActionDropdownOptions(step)"
              @select="val => dropDownActionChange(val, step)"
              :nodeProps="
                opt => {
                  return { 'aria-label': opt.label }
                }
              "
            />
          </div>
        </div>
      </div>

      <div class="mr-[8px] mt-[10px] w-full">
        <UITooltip :trigger="isCreateGroupDisabled ? 'hover' : 'manual'">
          <template #trigger>
            <UIButton
              id="new-category"
              type="tertiary"
              class="w-full bg-primary-50 px-3"
              size="small"
              :disabled="isCreateGroupDisabled"
              @click="openEditGroupModal(null)"
            >
              <div class="text-md flex items-center gap-1 font-normal">
                <PlusIcon class="h-4 w-4 text-primary-700" /><span class="mr-1">
                  {{ $t('settings.calendar.new_group') }}
                </span>
              </div>
            </UIButton>
          </template>
          <div class="max-w-[300px]">
            {{ $t('settings.calendar.create_group_access_disabled') }}
          </div>
        </UITooltip>
      </div>
    </div>

    <DeleteConfirmation
      v-model:show="showDeleteConfirmationModal"
      :disabled-confirm="isProcessing"
      :header-title="
        t('settings.calendar.delete_group') + ' ' + (selectedGroup?.name || '')
      "
      @on-confirm="deleteGroup"
      @on-modal-close="closeModal"
    >
      <template #message>
        <p class="mb-4 text-sm leading-5 text-gray-500">
          {{ $t('settings.calendar.delete_group_confirmation') }}
        </p>
        <div v-if="selectedGroup?.nosOfCalendars > 0">
          <UIDivider />
          <UICheckbox
            id="group-delete-model"
            v-model:checked="deleteCalendarsCheckBoxValue"
            class="mb-2"
          >
            <div class="ml-[2px] mt-[-1px]">
              <div class="text-black-600 text-base">
                {{
                  $t(
                    'settings.calendar.delete_group_confirmation_checkbox_title'
                  )
                }}
              </div>
              <p class="mt-[5px] text-sm leading-6 text-gray-500">
                {{
                  $t(
                    'settings.calendar.delete_group_confirmation_checkbox_description'
                  )
                }}
              </p>
            </div>
          </UICheckbox>
        </div>
      </template>
    </DeleteConfirmation>

    <GeneralConfirmation
      v-model:show="showDeactivateConfirmationModal"
      :disabled-confirm="isProcessing"
      :header-title="
        $t('settings.calendar.group_status_message_title', {
          status: selectedGroup?.isActive ? 'Deactivate' : 'Activate',
        })
      "
      @on-confirm="disableGroup"
      @on-modal-close="closeModal"
    >
      <template #message>
        <p class="text-sm leading-6 text-gray-500">
          {{
            selectedGroup?.isActive
              ? $t('settings.calendar.group_status_inactive_message')
              : $t('settings.calendar.group_status_active_message')
          }}
        </p>
      </template>
    </GeneralConfirmation>
    <CalendarShareModal
      v-if="showShareModal"
      :show="showShareModal"
      :data="selectedGroupData"
      @close-modal="closeShareCalendarPopup"
    />
  </div>
</template>
<style>
.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
